import API_BASE_URL from "../Constants";

// Key used to store the refresh token in localStorage
const REFRESH_TOKEN_KEY = 'refreshToken'; // Choose a suitable key name

// In-memory variable to hold the current access token
// This makes it easily accessible for API calls (e.g., fetch wrapper)
// but it won't persist across page refreshes. AuthContext will re-populate it.
let memoryAccessToken = null;

/**
 * Saves the access token to memory and the refresh token to localStorage.
 * @param {string | null} accessToken The JWT access token.
 * @param {string | null} refreshToken The JWT refresh token.
 */
export const setAuthTokens = (accessToken, refreshToken) => {
    if (accessToken) {
        memoryAccessToken = accessToken;
    } else {
        memoryAccessToken = null; // Clear if null is passed
    }

    if (refreshToken) {
        try {
            localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
        } catch (error) {
            console.error("Could not save refresh token to localStorage", error);
        }
    } else {
        // If null refreshToken is passed, remove it
        try {
            localStorage.removeItem(REFRESH_TOKEN_KEY);
        } catch (error) {
            console.error("Could not remove refresh token from localStorage", error);
        }
    }
};

/**
 * Retrieves the access token currently stored in memory.
 * Returns null if no token is in memory.
 * @returns {string | null} The access token or null.
 */
export const getAccessToken = () => {
    return memoryAccessToken;
};

/**
 * Directly sets the in-memory access token. Used primarily by AuthContext
 * or after token refresh.
 * @param {string | null} token The JWT access token.
 */
export const setMemoryAccessToken = (token) => {
    memoryAccessToken = token;
};


/**
 * Retrieves the refresh token from localStorage.
 * Returns null if no token is found.
 * @returns {string | null} The refresh token or null.
 */
export const getRefreshToken = () => {
    try {
        return localStorage.getItem(REFRESH_TOKEN_KEY);
    } catch (error) {
        console.error("Could not retrieve refresh token from localStorage", error);
        return null;
    }
};

/**
 * Clears the access token from memory and the refresh token from localStorage.
 */
export const clearAuthTokens = () => {
    memoryAccessToken = null;
    try {
        localStorage.removeItem(REFRESH_TOKEN_KEY);
    } catch (error) {
        console.error("Could not remove refresh token from localStorage during clear", error);
    }
};



/**
 * Calls the backend API to refresh the access token using a refresh token.
 * This function uses raw fetch, as it shouldn't trigger the authFetch interceptor.
 * @param {string} currentRefreshToken The currently stored refresh token.
 * @returns {Promise<{accessToken: string, refreshToken: string}>} Promise resolving with new tokens.
 * @throws {Error} If the refresh fails or the response is invalid.
 */
export const refreshToken = async (currentRefreshToken) => {
    if (!currentRefreshToken) {
        // If called without a token, immediately fail.
        return Promise.reject(new Error("No refresh token provided."));
    }

    const refreshUrl = `${API_BASE_URL}/api/auth/refresh`;


    try {
        const response = await fetch(refreshUrl, {
            method: 'POST',
            headers: {
                // No Authorization header needed for refresh endpoint usually
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            // Body should contain the refresh token
            body: JSON.stringify({ refreshToken: currentRefreshToken }),
        });

        // Try to parse JSON regardless of status for potential error messages
        const data = await response.json().catch(e => {
            console.error("Failed to parse refresh response body:", e);
            // Throw error based on HTTP status if parsing fails
            if (!response.ok) {
                throw new Error(`Token refresh failed with status ${response.status}`);
            }
            // If response was ok but body wasn't JSON (unlikely), handle appropriately
            throw new Error("Received non-JSON response from refresh endpoint");
        });


        if (!response.ok) {
            // Use error message from backend response if available
            const errorMessage = data?.message || data?.error || `HTTP error ${response.status}`;
            console.error(`Token refresh failed with status ${response.status}:`, errorMessage);
            throw new Error(`Token refresh failed: ${errorMessage}`);
        }

        // IMPORTANT: Check if backend response contains the expected new tokens
        if (data.tokens && data.user) {
            // Return the structure expected by AuthContext/authFetch
            return {
                accessToken: data.tokens.token,
                refreshToken: data.tokens.refreshToken,
                user: data.user,
            };
        } else {
            // Backend didn't return the expected tokens
            console.error("Invalid response structure from refresh token endpoint:", data);
            throw new Error("Invalid response structure from refresh token endpoint.");
        }

    } catch (error) {
        console.error("Error during token refresh fetch operation:", error);
        // Re-throw the error so the caller (authFetch's handleRefresh) knows it failed
        // The handleRefresh logic should then trigger logout.
        throw error;
    }
};
